import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';

import httpInstances from '@/api/axiosInstance';
import { DEFAULT_DASHBOARD_ID } from '@/features/Dashboard/constants/const';
import {
  DashboardResponse,
  DashboardStructure,
  MarketApp,
  MarketApps
} from '@/features/Dashboard/types';
import { extractMarketApps } from '@/features/Dashboard/utils/dashboardMigration';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import usePreferences from '@/pages/Profile/Preferences/usePreferences';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

export const useDashboard = (rename = true) => {
  return useQuery({
    queryKey: ['dashboard', rename],
    queryFn: async () => {
      const { data } =
        await httpInstances.axiosInternalInstance.get<Record<string, DashboardResponse>>(
          `/config/dashboard`
        );

      // Server returns an object keyed by dashboardName; extract MarketApps for each entry and add ids
      const result = Object.fromEntries(
        Object.entries(data).map(([name, dashboardData]) => {
          const marketApps = extractMarketApps(dashboardData);

          // Track used IDs encountered while processing to preserve existing unique IDs
          // and only generate new ones on actual collisions.
          const usedAppIds = new Set<string>();
          const usedTabIds = new Set<string>();

          const appsWithIds: MarketApp[] = marketApps.map(app => {
            if ('type' in app) {
              return {
                ...app,
                tiles: app.tiles.map(tile => ({
                  ...tile
                }))
              };
            }

            // Resolve app ID: keep if unique, otherwise generate a collision-free one
            let appId = app.id ?? '';
            if (!appId || (appId && usedAppIds.has(appId))) {
              appId = generateUniqueUUID(usedAppIds);
            } else {
              // reserve the existing unique id
              usedAppIds.add(appId);
            }

            const tabs = app.tabs.map(tab => {
              const existing = (tab as any).id as string | undefined;
              let tabId = existing ?? '';
              if (!tabId || usedTabIds.has(tabId)) {
                tabId = generateUniqueUUID(usedTabIds);
              } else {
                usedTabIds.add(tabId);
              }
              return {
                ...tab,
                id: tabId
              };
            });

            return {
              ...app,
              id: appId,
              tabs
            };
          });

          return [name === DEFAULT_DASHBOARD_ID && rename ? 'New Dashboard' : name, appsWithIds];
        })
      );

      return result;
    },
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity
  });
};

export const useUpdateDashboard = (
  dashboardName = 'New Dashboard',
  successMessage = 'Dashboard updated!',
  tabName = 'Main',
  oldDashboardName?: string
) => {
  const { showErrorSnackbar, showSuccessSnackbar, showLoadingSnackbar } =
    useSnackbarNotifications();
  const queryClient = useQueryClient();
  const { changeUserSetting, homepage } = usePreferences();
  const navigate = useNavigate();

  const { data } = useDashboard(false);

  return useMutation({
    mutationKey: ['dashboard', dashboardName, oldDashboardName],
    mutationFn: async (data: MarketApps | DashboardStructure) => {
      showLoadingSnackbar('Updating the dashboard, please wait…');

      // Determine payload format based on input
      let payload: DashboardStructure;
      if (Array.isArray(data)) {
        // Legacy MarketApps array - convert to nested structure
        payload = {
          [dashboardName]: {
            [tabName]: data
          }
        };
      } else {
        // Already in new structure format
        payload = data;
      }
      let response;
      if (oldDashboardName !== dashboardName && oldDashboardName) {
        response = await httpInstances.axiosInternalInstance.patch(
          `/config/dashboard/${encodeURIComponent(oldDashboardName)}`,
          payload,
          {
            params: {
              newKey: dashboardName
            }
          }
        );
      } else {
        response = await httpInstances.axiosInternalInstance.post(
          `/config/dashboard/${encodeURIComponent(dashboardName)}`,
          payload
        );
      }

      // Wait 2 seconds after successful response
      await new Promise(resolve => {
        setTimeout(resolve, 2000);
      });

      return response;
    },
    onError: () => {
      showErrorSnackbar("Oops! We couldn't save your dashboard. Please try again.");
    },
    onSuccess: async () => {
      if (successMessage) {
        showSuccessSnackbar(successMessage);
      }
      if (data?.[DEFAULT_DASHBOARD_ID]) {
        await httpInstances.axiosInternalInstance.delete(
          `/config/dashboard/${DEFAULT_DASHBOARD_ID}`
        );
        await new Promise(resolve => {
          setTimeout(resolve, 2000);
        });
      }

      if (oldDashboardName !== dashboardName) {
        queryClient.resetQueries({ queryKey: ['dashboard'] });
        navigate(`/dashboard/${encodeURIComponent(dashboardName)}`);
      }
      if (homepage.value !== 'my-dashboard') {
        changeUserSetting('homepage', {
          label: 'My Dashboard',
          value: 'my-dashboard'
        });
      }
      queryClient.refetchQueries({ queryKey: ['dashboard'] });
    }
  });
};
